import { yupResolver } from '@hookform/resolvers/yup';
import { queryClient } from 'providers/queryClient';
import { useNotify } from 'react-admin';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from 'shared/mui/Buttons';
import { Card, CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog, DialogProps } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { FileInput } from 'shared/react-admin/Inputs/FileInput/FileInput';
import { ACTIVE_IMPORT_ID_KEY } from 'shared/widgets/RequestsBankOpsImport/hooks/useGetActiveImportId';

import { validationSchema } from './schemas/schema';
import { useImportFiles } from '../../hooks/import/useImportFiles';

const IMPORT_TEMPLATE_FILE = 'template.xlsx';

type Props = DialogProps & {
  onError: (err: any) => void;
};

type FormValues = {
  file: File;
};

export const RequestsBankOpsListImportDialog: React.FC<Props> = (props) => {
  const notify = useNotify();
  const importFiles = useImportFiles();

  const form = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      file: null as unknown as File,
    },
  });
  const file = form.watch('file');

  const handleClickSaveTemplate = () => {
    const link = document.createElement('a');
    link.href = `/${IMPORT_TEMPLATE_FILE}`;
    link.download = IMPORT_TEMPLATE_FILE;
    link.click();
  };

  const onSubmit = async (data: any) => {
    if (data.file) {
      const formData = new FormData();
      formData.append('file', data.file);
      try {
        await importFiles.mutateAsync(formData);
        await queryClient.invalidateQueries({ queryKey: [ACTIVE_IMPORT_ID_KEY] });

        props.onClose();
        notify('Файл принят. Выполняется проверка и загрузка данных', { type: 'success' });
      } catch (error: any) {
        props.onClose();
        props.onError(error);
      }
    }
  };

  const isLoading = importFiles.isPending;

  return (
    <Dialog {...props} width="640px">
      <Card>
        <CardHeader
          appendActions={[
            <CloseIconButton disabled={isLoading} key="close-button" onClose={props.onClose} />,
          ]}
          sx={{ height: 'auto' }}
          title="Импорт заявок из Excel"
          titleSize="smallBold"
        />
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <CardContent>
              <FileInput disabled={isLoading} name="file" />
            </CardContent>
            <CardActionsButtons
              rightActionsSlot={[
                <Button
                  disabled={isLoading}
                  key="download-button"
                  label="Скачать шаблон для загрузки"
                  onClick={handleClickSaveTemplate}
                  variant="outlined"
                />,
                <Button
                  disabled={!file || isLoading || !form.formState.isValid}
                  isLoading={isLoading}
                  key="next-button"
                  label="Продолжить"
                  type="submit"
                  variant="contained"
                />,
              ]}
            />
          </form>
        </FormProvider>
      </Card>
    </Dialog>
  );
};
